import { Fragment, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DescriptionIcon from '@mui/icons-material/Description';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { BarChart } from '@mui/x-charts/BarChart';
import { api, authedDownloadUrl } from '../lib/api';
import { formatCents, formatTimestamp } from '../lib/format';
import { monthLabel, periodRange, PERIOD_OPTIONS, type PeriodKey } from '../lib/period';
import { downloadCsv } from '../lib/csv';
import { csvName, postingsCsv, summaryCsv, type ExportPosting } from '../lib/reportCsv';
import { PageHelp } from '../components/PageHelp';
import type { Division, TaxAreaEntry } from '../lib/types';

// Zwei Reihen, Farben aus der validierten Kategorie-Palette (blau/orange, CVD-geprüft).
const INCOME_COLOR = '#2a78d6';
const EXPENSE_COLOR = '#eb6834';

interface GroupLine {
  groupId: string;
  name: string;
  side: 'einnahme' | 'ausgabe';
  cents: number;
  priorCents: number;
  accounts: { number: string; name: string; cents: number }[];
}

interface ReportResponse {
  report: {
    from: string;
    to: string;
    totals: { income: number; expense: number; saldo: number };
    prior: { income: number; expense: number; saldo: number };
    open: { count: number; cents: number };
    divisions: {
      divisionId: string;
      name: string;
      income: number;
      expense: number;
      saldo: number;
      priorSaldo: number;
      openCount: number;
    }[];
    groups: GroupLine[];
    months: { month: string; income: number; expense: number }[];
    tags: TagLine[];
  };
  divisions: Division[];
  taxAreas: TaxAreaEntry[];
  dataAsOf: string | null;
}

interface TagLine {
  tagId: string;
  name: string;
  count: number;
  income: number;
  expense: number;
  saldo: number;
}

interface PostingRow extends ExportPosting {
  postingId: string;
  fiscalYear: string;
  hasDocument: boolean;
}

interface Drill {
  title: string;
  query: Record<string, string>;
}

const euro = (cents: number) => formatCents(cents);
const germanDate = (iso: string) => iso.split('-').reverse().join('.');

/** Für den Dateinamen: Abteilungs- und Tag-Name statt interner ID. */
function fileQuery(
  query: Record<string, string>,
  divisions: Division[] | undefined,
  tags: TagLine[] = [],
) {
  const named = { ...query };
  if (query.division) named.division = divisionNames(divisions)[query.division] ?? query.division;
  if (query.tag) named.tag = tags.find((t) => t.tagId === query.tag)?.name ?? query.tag;
  return named;
}

/** Monate, bei langen Zeiträumen (z.B. "Alle Jahre") zu Jahren zusammengefasst. */
function periods(months: { month: string; income: number; expense: number }[]) {
  if (months.length <= 24) return months;
  const years = new Map<string, { month: string; income: number; expense: number }>();
  for (const m of months) {
    const y = m.month.slice(0, 4);
    const e = years.get(y) ?? { month: y, income: 0, expense: 0 };
    e.income += m.income;
    e.expense += m.expense;
    years.set(y, e);
  }
  return [...years.values()];
}

function divisionNames(divisions: Division[] | undefined) {
  return Object.fromEntries((divisions ?? []).map((d) => [d.divisionId, d.name]));
}

function Kpi({ label, value, prior }: { label: string; value: number; prior?: number }) {
  return (
    <Paper sx={{ p: 2, flex: 1, minWidth: 180 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {euro(value)}
      </Typography>
      {prior !== undefined && (
        <Typography variant="caption" color="text.secondary">
          Vorjahr: {euro(prior)}
        </Typography>
      )}
    </Paper>
  );
}

/**
 * FR-21 ff.: Einnahmen, Ausgaben, Saldo nach Zahlungsdatum, gegliedert nach Report-Gruppen, mit
 * Monatsverlauf, Vorjahresvergleich, offenen Posten und Drill-down bis zum Beleg. Die API liefert
 * nur Abteilungen, die der Aufrufer sehen darf (FR-29).
 */
export default function DashboardPage() {
  const today = new Date().toISOString().slice(0, 10);
  const thisYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const [period, setPeriod] = useState<PeriodKey>('jahr');
  const [division, setDivision] = useState('');
  const [taxArea, setTaxArea] = useState('');
  const [tag, setTag] = useState('');
  /** Namen aller bisher geladenen Tags -- für einen gewählten Tag, der gerade nicht vorkommt. */
  const [tagNames, setTagNames] = useState<Record<string, string>>({});
  const [data, setData] = useState<ReportResponse>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [asTable, setAsTable] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [drill, setDrill] = useState<Drill>();
  const [exporting, setExporting] = useState(false);

  const { from, to } = periodRange(year, period, today);
  const baseQuery = useMemo(() => {
    const q: Record<string, string> = { from, to };
    if (division) q.division = division;
    if (taxArea) q.taxArea = taxArea;
    if (tag) q.tag = tag;
    return q;
  }, [from, to, division, taxArea, tag]);

  useEffect(() => {
    setLoading(true);
    setError(undefined);
    api
      .get<ReportResponse>(`/admin/report?${new URLSearchParams(baseQuery)}`)
      .then((res) => {
        setData(res);
        setTagNames((known) => ({
          ...known,
          ...Object.fromEntries(res.report.tags.map((t) => [t.tagId, t.name])),
        }));
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Report konnte nicht geladen werden'),
      )
      .finally(() => setLoading(false));
  }, [baseQuery]);

  const r = data?.report;
  const tags = r?.tags ?? [];
  // Ein gewählter Tag bleibt auswählbar, auch wenn er unter den übrigen Filtern nichts mehr hat.
  const tagOptions =
    tag && !tags.some((t) => t.tagId === tag)
      ? [
          ...tags,
          {
            tagId: tag,
            name: tagNames[tag] ?? tag,
            count: 0,
            income: 0,
            expense: 0,
            saldo: 0,
          },
        ]
      : tags;

  // FR-30: alle Buchungen des aktuellen Filters bzw. die Summenansicht als CSV.
  async function exportPostings() {
    setExporting(true);
    try {
      const rows = await api.get<PostingRow[]>(
        `/admin/report/postings?${new URLSearchParams(baseQuery)}`,
      );
      downloadCsv(
        csvName('buchungen', fileQuery(baseQuery, data?.divisions, tags)),
        postingsCsv(rows, divisionNames(data?.divisions)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  }

  function exportSummary() {
    if (r)
      downloadCsv(
        csvName('summen', fileQuery(baseQuery, data?.divisions, tags)),
        summaryCsv(r.groups, r.totals, r.prior),
      );
  }

  const multiDivision = (data?.divisions.length ?? 0) > 1;
  const groupsBySide = (side: GroupLine['side']) =>
    (r?.groups ?? []).filter((g) => g.side === side);
  const months = periods(r?.months ?? []);
  const byYear = months.length !== (r?.months ?? []).length;
  const chartData = months.map((m) => ({
    label: monthLabel(m.month),
    month: m.month,
    income: m.income / 100,
    // Ausgaben als Betrag, damit beide Balken von der Nulllinie nach oben zeigen.
    expense: -m.expense / 100,
  }));

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h4">Dashboard</Typography>
        <PageHelp text="Einnahmen und Ausgaben netto (ohne Umsatzsteuer) nach Zahlungsdatum aus SevDesk, je Abteilung und gegliedert nach Gruppen. Ein Klick auf eine Gruppe, ein Konto oder einen Monat zeigt die einzelnen Buchungen samt Beleg. Mit dem Filter #Tag (in SevDesk am Beleg gesetzt, z.B. eine Veranstaltung) gelten alle Zahlen nur für diese Belege; der Saldo ist dann das Ergebnis, mit Zeitraum Alle Jahre auch über den Jahreswechsel." />
      </Stack>

      {/* Filter in einer Zeile oberhalb der Zahlen */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          size="small"
          label="Jahr"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          disabled={period === 'alle'}
          sx={{ minWidth: 110 }}
        >
          {Array.from({ length: thisYear - 2017 }, (_, i) => thisYear - i).map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Zeitraum"
          value={period}
          onChange={(e) => setPeriod(e.target.value as PeriodKey)}
          sx={{ minWidth: 160 }}
        >
          {PERIOD_OPTIONS.map((p) => (
            <MenuItem key={p.key} value={p.key}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
        {multiDivision && (
          <TextField
            select
            size="small"
            label="Abteilung"
            value={division}
            onChange={(e) => setDivision(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Alle Abteilungen</MenuItem>
            {data!.divisions.map((d) => (
              <MenuItem key={d.divisionId} value={d.divisionId}>
                {d.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          size="small"
          label="Steuerlicher Bereich"
          value={taxArea}
          onChange={(e) => setTaxArea(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Alle Bereiche</MenuItem>
          {(data?.taxAreas ?? []).map((t) => (
            <MenuItem key={t.key} value={t.key}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="#Tag"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Alle #Tags</MenuItem>
          {tagOptions.map((t) => (
            <MenuItem key={t.tagId} value={t.tagId}>
              {t.name} ({t.count})
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {data && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
        >
          <Typography variant="body2" color="text.secondary">
            {germanDate(from)} bis {germanDate(to)} · Beträge netto · Datenstand:{' '}
            {data.dataAsOf ? formatTimestamp(data.dataAsOf) : 'noch kein Import'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<FileDownloadIcon />}
              disabled={!r || loading}
              onClick={exportSummary}
            >
              Summen als CSV
            </Button>
            <Button
              size="small"
              startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
              disabled={!r || loading || exporting}
              onClick={() => void exportPostings()}
            >
              Buchungen als CSV
            </Button>
          </Stack>
        </Stack>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {loading && !r && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}

      {r && (
        <>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ opacity: loading ? 0.5 : 1 }}
          >
            <Kpi label="Einnahmen" value={r.totals.income} prior={r.prior.income} />
            <Kpi label="Ausgaben" value={r.totals.expense} prior={r.prior.expense} />
            <Kpi label="Saldo" value={r.totals.saldo} prior={r.prior.saldo} />
            <Kpi label={`Offen (${r.open.count} Belege)`} value={r.open.cents} />
          </Stack>

          {!tag && tags.length > 0 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nach #Tags</TableCell>
                    <TableCell align="right">Buchungen</TableCell>
                    <TableCell align="right">Einnahmen</TableCell>
                    <TableCell align="right">Ausgaben</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tags.map((t) => (
                    <TableRow
                      key={t.tagId}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setTag(t.tagId)}
                    >
                      <TableCell>{t.name}</TableCell>
                      <TableCell align="right">{t.count}</TableCell>
                      <TableCell align="right">{euro(t.income)}</TableCell>
                      <TableCell align="right">{euro(t.expense)}</TableCell>
                      <TableCell align="right">
                        <strong>{euro(t.saldo)}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {multiDivision && !division && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Abteilung</TableCell>
                    <TableCell align="right">Einnahmen</TableCell>
                    <TableCell align="right">Ausgaben</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell align="right">Saldo Vorjahr</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {r.divisions.map((d) => (
                    <TableRow
                      key={d.divisionId}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setDivision(d.divisionId)}
                    >
                      <TableCell>{d.name}</TableCell>
                      <TableCell align="right">{euro(d.income)}</TableCell>
                      <TableCell align="right">{euro(d.expense)}</TableCell>
                      <TableCell align="right">
                        <strong>{euro(d.saldo)}</strong>
                      </TableCell>
                      <TableCell align="right">{euro(d.priorSaldo)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <strong>Verein gesamt</strong>
                    </TableCell>
                    <TableCell align="right">{euro(r.totals.income)}</TableCell>
                    <TableCell align="right">{euro(r.totals.expense)}</TableCell>
                    <TableCell align="right">
                      <strong>{euro(r.totals.saldo)}</strong>
                    </TableCell>
                    <TableCell align="right">{euro(r.prior.saldo)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Paper sx={{ p: 2 }}>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{byYear ? 'Jahresverlauf' : 'Monatsverlauf'}</Typography>
              <FormControlLabel
                control={
                  <Switch checked={asTable} onChange={(e) => setAsTable(e.target.checked)} />
                }
                label="Als Tabelle"
              />
            </Stack>
            {asTable ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Monat</TableCell>
                    <TableCell align="right">Einnahmen</TableCell>
                    <TableCell align="right">Ausgaben</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {months.map((m) => (
                    <TableRow
                      key={m.month}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() =>
                        setDrill({ title: monthLabel(m.month), query: { month: m.month } })
                      }
                    >
                      <TableCell>{monthLabel(m.month)}</TableCell>
                      <TableCell align="right">{euro(m.income)}</TableCell>
                      <TableCell align="right">{euro(m.expense)}</TableCell>
                      <TableCell align="right">{euro(m.income + m.expense)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: Math.max(360, chartData.length * 56) }}>
                  <BarChart
                    dataset={chartData}
                    height={300}
                    borderRadius={4}
                    grid={{ horizontal: true }}
                    xAxis={[{ scaleType: 'band', dataKey: 'label', categoryGapRatio: 0.4 }]}
                    yAxis={[
                      {
                        valueFormatter: (v: number | null) =>
                          `${(v ?? 0).toLocaleString('de-DE')} €`,
                      },
                    ]}
                    series={[
                      {
                        dataKey: 'income',
                        label: 'Einnahmen',
                        color: INCOME_COLOR,
                        valueFormatter: (v: number | null) => euro(Math.round((v ?? 0) * 100)),
                      },
                      {
                        dataKey: 'expense',
                        label: 'Ausgaben',
                        color: EXPENSE_COLOR,
                        valueFormatter: (v: number | null) => euro(Math.round((v ?? 0) * 100)),
                      },
                    ]}
                    slotProps={{
                      legend: {
                        direction: 'horizontal',
                        position: { vertical: 'top', horizontal: 'end' },
                      },
                    }}
                    onAxisClick={(_e, d) => {
                      const m = d?.dataIndex !== undefined ? chartData[d.dataIndex] : undefined;
                      if (m) setDrill({ title: m.label, query: { month: m.month } });
                    }}
                  />
                </Box>
              </Box>
            )}
          </Paper>

          {(['einnahme', 'ausgabe'] as const).map((side) => (
            <TableContainer component={Paper} key={side}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {side === 'einnahme' ? 'Einnahmen' : 'Ausgaben'} nach Gruppen
                    </TableCell>
                    <TableCell align="right">Betrag</TableCell>
                    <TableCell align="right">Vorjahr</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupsBySide(side).map((g) => (
                    <Fragment key={g.groupId}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            aria-label="Konten anzeigen"
                            onClick={() => setOpen({ ...open, [g.groupId]: !open[g.groupId] })}
                          >
                            {open[g.groupId] ? (
                              <KeyboardArrowDownIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowRightIcon fontSize="small" />
                            )}
                          </IconButton>
                          <Button
                            size="small"
                            onClick={() => setDrill({ title: g.name, query: { group: g.groupId } })}
                          >
                            {g.name}
                          </Button>
                        </TableCell>
                        <TableCell align="right">{euro(g.cents)}</TableCell>
                        <TableCell align="right">{euro(g.priorCents)}</TableCell>
                      </TableRow>
                      {open[g.groupId] &&
                        g.accounts.map((a) => (
                          <TableRow
                            key={a.number}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() =>
                              setDrill({
                                title: `${a.number} ${a.name}`,
                                query: { account: a.number },
                              })
                            }
                          >
                            <TableCell sx={{ pl: 9 }}>
                              {a.number} {a.name}
                            </TableCell>
                            <TableCell align="right">{euro(a.cents)}</TableCell>
                            <TableCell />
                          </TableRow>
                        ))}
                    </Fragment>
                  ))}
                  {groupsBySide(side).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography color="text.secondary">Keine Buchungen im Zeitraum.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ))}
        </>
      )}

      {drill && (
        <DrillDialog
          drill={drill}
          baseQuery={baseQuery}
          divisions={data?.divisions}
          onClose={() => setDrill(undefined)}
        />
      )}
    </Stack>
  );
}

function DrillDialog({
  drill,
  baseQuery,
  divisions,
  onClose,
}: {
  drill: Drill;
  baseQuery: Record<string, string>;
  divisions?: Division[];
  onClose: () => void;
}) {
  const [rows, setRows] = useState<PostingRow[]>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    api
      .get<PostingRow[]>(
        `/admin/report/postings?${new URLSearchParams({ ...baseQuery, ...drill.query })}`,
      )
      .then(setRows)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen'),
      );
  }, [drill, baseQuery]);

  async function openDocument(row: PostingRow) {
    // Fenster sofort öffnen, sonst blockt der Browser das Pop-up nach dem await.
    const win = window.open('', '_blank');
    try {
      const url = await authedDownloadUrl(
        `/admin/report/document/${row.fiscalYear}?id=${encodeURIComponent(row.postingId)}`,
      );
      if (win) win.location.assign(url);
      else window.location.assign(url);
    } catch (err) {
      win?.close();
      setError(err instanceof Error ? err.message : 'Beleg konnte nicht geladen werden');
    }
  }

  const sum = (rows ?? []).reduce((s, row) => s + row.amountCents, 0);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Buchungen: {drill.title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!rows && !error && <CircularProgress />}
        {rows && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Datum</TableCell>
                  <TableCell>Beleg</TableCell>
                  <TableCell>Partner / Text</TableCell>
                  <TableCell>Konto</TableCell>
                  <TableCell>KSt</TableCell>
                  <TableCell>#Tag</TableCell>
                  <TableCell align="right">Betrag</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.postingId} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{germanDate(row.stichtag)}</TableCell>
                    <TableCell>{row.voucherNumber ?? '—'}</TableCell>
                    <TableCell>
                      {row.partner}
                      {row.text && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {row.text}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.accountNumber} {row.accountName}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.costCentreName ?? row.costCentreNumber}
                    </TableCell>
                    <TableCell>{row.tagName}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {euro(row.amountCents)}
                    </TableCell>
                    <TableCell>
                      {row.hasDocument && (
                        <Tooltip title="Beleg öffnen">
                          <IconButton size="small" onClick={() => void openDocument(row)}>
                            <DescriptionIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={6}>
                    <strong>{rows.length} Buchungen</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{euro(sum)}</strong>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<FileDownloadIcon />}
          disabled={!rows?.length}
          onClick={() =>
            downloadCsv(
              csvName('buchungen', fileQuery(baseQuery, divisions), drill.title),
              postingsCsv(rows ?? [], divisionNames(divisions)),
            )
          }
        >
          Als CSV
        </Button>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}
